import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useGetWarehouseProductsForSelectionMutation,
  useTransferProductsMutation,
} from '@app/api/Products';
import type {
  ModelProduct,
  ModelProductsTransfer,
  ModelProductTransfer,
} from '@app/api/Products';
import { WAREHOUSE_OPERATIONS_PATH } from '@app/constants';
import { useWarehouseOperationData } from '@app/pages/Warehouses/WarehouseOperations/components/ProductOperation/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useErrorHandler } from '@app/shared/hooks';
import type { Options } from '@app/types';

const { useForm, useWatch } = Form;

export const useGoodsTransfer = () => {
  const {
    ActiveBranchId,
    products,
    warehousesOptions,
    suppliersOptions,
    isPageLoading,
    errors,
  } = useWarehouseOperationData();

  const [isOpenAddAvailableProductsModal, setIsOpenAddAvailableProductsModal] =
    useState(false);
  const onOpenAddAvailableProductsModal = useCallback(() => {
    setIsOpenAddAvailableProductsModal(true);
  }, []);
  const onCloseAddAvailableProductsModal = useCallback(() => {
    setIsOpenAddAvailableProductsModal(false);
  }, []);

  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [form] = useForm<ModelProductsTransfer>();
  const { resetFields, setFieldValue } = form;
  const formValues = useWatch([], form);
  const [selectedProducts, setSelectedProducts] = useState<ModelProduct[]>([]);

  const [transferProducts, { isLoading: isLoadingTransferProducts }] =
    useTransferProductsMutation();

  const [
    getWarehouseProductsForSelection,
    {
      isError: isErrorWarehouseProducts,
      error: errorWarehouseProducts,
      isLoading: isLoadingWarehouseProducts,
      isUninitialized: isUninitializedWarehouseProducts,
      data: warehouseProducts = [],
    },
  ] = useGetWarehouseProductsForSelectionMutation();

  useErrorHandler([
    ...errors,
    { flag: isErrorWarehouseProducts, error: errorWarehouseProducts },
  ]);

  const warehouseFromId = useMemo(
    () => formValues?.WarehouseFromId,
    [formValues?.WarehouseFromId]
  );

  const warehouseToId = useMemo(
    () => formValues?.WarehouseToId,
    [formValues?.WarehouseToId]
  );

  const warehousesFromOptions: Options = useMemo(
    () => warehousesOptions.filter(({ value }) => value !== warehouseToId),
    [warehousesOptions, warehouseToId]
  );

  const warehousesToOptions: Options = useMemo(
    () => warehousesOptions.filter(({ value }) => value !== warehouseFromId),
    [warehouseFromId, warehousesOptions]
  );

  const isProductsLoading = useMemo(
    () => isLoadingWarehouseProducts || isUninitializedWarehouseProducts,
    [isLoadingWarehouseProducts, isUninitializedWarehouseProducts]
  );

  const getWarehouseProductsForSelectionHandler = useCallback(async () => {
    if (!warehouseFromId) return;
    try {
      await getWarehouseProductsForSelection({
        data: {
          WarehouseId: warehouseFromId,
          SearchTerm: null,
          ProductIds: null,
          SupplierIds: null,
          PriceOfSaleS: null,
          PriceOfSaleE: null,
        },
        ActiveBranchId,
      }).unwrap();
    } catch (error) {
      showNotification(error, 'warning');
    }
  }, [
    getWarehouseProductsForSelection,
    showNotification,
    warehouseFromId,
    ActiveBranchId,
  ]);

  const onFinish = useCallback(
    async (data: ModelProductsTransfer) => {
      try {
        await transferProducts({ data, ActiveBranchId }).unwrap();
        showNotification('Товары успешно перемещены.', 'success');
        resetFields();
        setSelectedProducts([]);
        navigate(WAREHOUSE_OPERATIONS_PATH);
      } catch (error) {
        showNotification(error, 'warning');
      }
    },
    [transferProducts, resetFields, showNotification, ActiveBranchId, navigate]
  );

  const onAdd = useCallback(
    (selected: ModelProduct[]) => {
      const products: ModelProductTransfer[] = warehouseProducts
        .filter((p) => selected.some((sp) => sp.Id === p.ProductId))
        .map((p) => ({
          ProductId: p.ProductId,
          SupplierId: p.SupplierId,
          PriceOfSale: p.PriceOfSale,
          Count: p.CountAvailable,
        }));
      setFieldValue('Products', products);
      setSelectedProducts(selected);
      onCloseAddAvailableProductsModal();
    },
    [onCloseAddAvailableProductsModal, setFieldValue, warehouseProducts]
  );

  const totalCount = useMemo(() => {
    const list = formValues?.Products ?? [];
    return list.reduce((acc, cur) => acc + Number(cur?.Count ?? 0), 0);
  }, [formValues]);

  const onDelete = useCallback((productId: number) => {
    setSelectedProducts((prev) => prev.filter((p) => p.Id !== productId));
  }, []);

  return {
    form,
    onFinish,
    isPageLoading,
    getWarehouseProductsForSelectionHandler,
    warehousesFromOptions,
    warehousesToOptions,
    warehouseProducts,
    isOpenAddAvailableProductsModal,
    onCloseAddAvailableProductsModal,
    onOpenAddAvailableProductsModal,
    onAdd,
    suppliersOptions,
    selectedProducts,
    products,
    formValues,
    totalCount,
    onDelete,
    setSelectedProducts,
    isProductsLoading,
    isLoadingTransferProducts,
    warehousesOptions,
  };
};
